// Copy + per-type formatting for the AlertRule entity. Centralized here so
// the StageBadge / threshold input / test-fire result share one source of
// truth — a future i18n pass replaces this module with a localized
// equivalent and every consumer follows.

import type {
  AlertTestFireReason,
  AlertTypeMvp,
} from "@community-analytics/shared";

export const ALERT_TYPE_LABELS: Record<AlertTypeMvp, string> = {
  HEALTH_DROP: "Health Score drop",
  MEMBER_DROP: "Member loss",
  ACTIVITY_SPIKE: "Activity spike",
};

export const ALERT_TYPE_DESCRIPTIONS: Record<AlertTypeMvp, string> = {
  HEALTH_DROP:
    "Notifies when your community's health crosses below this threshold.",
  MEMBER_DROP:
    "Notifies when more than this fraction of members leave in 24 hours.",
  ACTIVITY_SPIKE:
    "Notifies when today's messages exceed this multiple of the 14-day median.",
};

/**
 * Threshold display unit per type. HEALTH_DROP is a 0-100 score; MEMBER_DROP
 * is a 0-1 fraction (rendered as a percentage); ACTIVITY_SPIKE is a
 * multiplier (rendered with an "×" suffix).
 */
export function formatThresholdValue(
  type: AlertTypeMvp,
  threshold: number,
): string {
  switch (type) {
    case "HEALTH_DROP":
      return String(Math.round(threshold));
    case "MEMBER_DROP":
      return `${Math.round(threshold * 100)}%`;
    case "ACTIVITY_SPIKE":
      return `${threshold.toFixed(1)}×`;
  }
}

export function thresholdInputUnitSuffix(type: AlertTypeMvp): string {
  switch (type) {
    case "HEALTH_DROP":
      return "/100";
    case "MEMBER_DROP":
      return "%";
    case "ACTIVITY_SPIKE":
      return "×";
  }
}

/**
 * For MEMBER_DROP the user enters a percentage (0..100) but the API expects
 * a 0..1 fraction. Centralizing this conversion in two helpers means the
 * threshold form and the threshold display agree on the conversion shape.
 */
export function thresholdToFormValue(
  type: AlertTypeMvp,
  threshold: number,
): number {
  if (type === "MEMBER_DROP") return Math.round(threshold * 100);
  return threshold;
}

export function formValueToThreshold(
  type: AlertTypeMvp,
  formValue: number,
): number {
  if (type === "MEMBER_DROP") return formValue / 100;
  return formValue;
}

export const TEST_FIRE_REASON_LABELS: Record<AlertTestFireReason, string> = {
  would_fire: "This alert would fire right now.",
  would_not_fire: "This alert would NOT fire right now.",
  insufficient_history: "Not enough history yet — need 7 days of data.",
  insufficient_data: "Not enough recent data to evaluate.",
  rule_disabled: "Enable the rule to run a test-fire.",
};
